import { useCallback, useEffect, useMemo, useRef, useState, ChangeEvent, DragEvent } from "react"
import { GetApp } from "../../App"
import { GetCruise, GetShip, GetShipContext, GetTrip } from "../../App/Context"
import { AppViewData } from "../App/Context"
import { logger, fieldNames } from "../../App/Logger"
import { useEditState } from "../../App/useEditState"
import { classNames } from "../../Components"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { EditGroup } from "../../Components/EditGroup"
import { AutocompleteInput } from "../../Components/Input"
import { INoteAttachment, INoteParentType, INoteType } from "../../Types"
import { DataStore, required, useDataStore, useEntity, useQuery, useRetainedEntities } from "../../Data"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"
import { dayToDate } from "../DayView"
import { GetShipView, GetShipUsers } from "../ShipView"
import { GetNoteParent, NoteOwnedByParent, NoteParent, NoteViewData } from "./Context"
import { NoteAttachments } from "./Attachments"
import { NoteViewProps } from "./Params"
import { useNoteWriters } from "./writers"

export function NoteView({ editable, isCurrentTrip, note, noteType, parentType }: NoteViewProps) {
  const app = GetApp();
  const ship = GetShip();
  const contextStore = useDataStore() as DataStore | undefined;
  const [fallbackStore] = useState(() => new DataStore());
  const store = contextStore ?? fallbackStore;
  const writers = useNoteWriters(ship.id);
  useState(() => {
    if (note) {
      store.applyBundle({
        entities: {
          Note: [note],
          NoteAttachment: note.attachments ?? [],
        },
      });
    }
    return true;
  });
  const storedNote = useEntity(store, SchemaEntities.Note, note?.id ?? 0);
  const storedAttachments = useQuery(store, SchemaEntities.NoteAttachment, {
    noteId: required(note?.id),
  });
  useRetainedEntities(
    [SchemaEntities.Note, SchemaEntities.NoteAttachment],
    note !== undefined,
    note?.id,
  );
  useEffect(() => {
    if (note) {
      store.applyBundle({
        entities: {
          Note: [note],
          NoteAttachment: note.attachments ?? [],
        },
      });
    }
  }, [note, store]);
  const canonicalNote = storedNote
    ? { ...storedNote, attachments: [...storedAttachments] }
    : note;
  const { apiUser } = GetShipContext();
  const shipUsers = GetShipUsers();
  const { shipCloudEditable } = GetShipView();
  const [parentCruise, parentTrip, parentDay] = GetNoteParent(parentType);
  // Full trip/cruise contexts (with their day/trip lists) read at the top level
  // so `parentInfo` below doesn't call these context hooks inside a useMemo.
  const tripDays = GetTrip()?.days;
  const cruiseTrips = GetCruise()?.trips;
  const [edited, onEdited, resetEdited] = useEditState();
  const t = app.getT("NoteView");
  const editableFields = ["date", "note", "noteExtra", { name: "noteType", type: "int" }, "title"];
  const contentFields = NoteViewData.noteContentFields(noteType);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<INoteAttachment[]>([]);
  const attachments = useMemo(() => {
    const persisted = canonicalNote?.attachments || [];
    const persistedIds = new Set(persisted.map((attachment) => attachment.id));
    return [...persisted, ...uploadedAttachments.filter((attachment) => !persistedIds.has(attachment.id))];
  }, [canonicalNote?.attachments, uploadedAttachments]);
  const [autocompleteWeatherNoteTitle, setAutocompleteWeatherNoteTitle] = useState<string[] | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const formEditable = useMemo(() => shipCloudEditable || (app.editable && editable != false && AppViewData.availableCurrentOrAll(apiUser, ship.id, isCurrentTrip)), []);

  useEffect(() => {
    const load = async () => {
      if (noteType === INoteType.weather && !autocompleteWeatherNoteTitle) {
        const weatherNoteTitleData = await NoteViewData.loadWeatherNoteTitleAutocomplete(app.apiClient, ship.id);
        setAutocompleteWeatherNoteTitle(weatherNoteTitleData?.values || []);
      }
    };
    load().catch(console.error);
  }, [noteType, ship.id, autocompleteWeatherNoteTitle]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const saveEdits = useCallback(async (_data: FormData, values: any) => {
    logger.debug("saving", fieldNames(values));
    if (!canonicalNote) {
      // #1137: an uncreated blank-day draft -- create the Day first (idempotent
      // server-side, so a second note-type tab's own draft save races safely) if it
      // doesn't exist yet, then the note itself, together in this one explicit save.
      let parent = NoteParent(parentType, parentCruise, parentDay, parentTrip);
      if (parentType == INoteParentType.day && parentDay && !parentDay.id) {
        const dayResult = await writers.createDay(parentDay.tripId, parentDay.day);
        parent = dayResult.day;
      }
      if (!parent) {
        return;
      }
      const result = await writers.createNote(parentType, parent.id, values);
      if (result && result.note) {
        resetEdited();
      }
      return;
    }
    const result = await writers.updateNote(canonicalNote.id, values);
    if (result && result.note) {
      // updated
      resetEdited();
    }
  }, [canonicalNote, parentType, parentCruise, parentDay, parentTrip, writers]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const saveAttachments = useCallback(async (_data: FormData, _values: any) => {
    if (!canonicalNote) {
      return;
    }
    for (const file of files) {
      const result = await writers.uploadAttachment(canonicalNote.id, file);
      const attachment = result.attachment;
      if (attachment) {
        setUploadedAttachments((current) => [...current.filter((item) => item.id !== attachment.id), attachment]);
      }
    }
    setFiles([]);
  }, [canonicalNote, files, writers]);

  const handleDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const newFiles: File[] = Array.from(droppedFiles);
      setFiles([...files, ...newFiles]);
    }
  }, [files]);

  const handleSelect = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (event.target.files && event.target.files.length > 0) {
      const newFiles: File[] = Array.from(event.target.files);
      setFiles([...files, ...newFiles]);
      // event.target.reset();
    }
  }, [files]);

  const parentInfo = useMemo(() => {
    if (canonicalNote?.dayId != undefined) {
      if (parentType != INoteParentType.cruise) {
        const day = tripDays?.find((d) => d.id == canonicalNote.dayId);
        if (day) {
          return t("note_parent_day", { date: app.format("dateInYear", dayToDate(day.day).toISOString()) });
        }
      }
    } else if (canonicalNote?.tripId != undefined) {
      const trip = cruiseTrips?.find((t) => t.id == canonicalNote.tripId);
      if (trip) {
        return t("note_parent_trip", { name: trip.title });
      }
    }
    return undefined;
  }, [canonicalNote, parentType, tripDays, cruiseTrips]);

  const fieldPrefix = useMemo(() => {
    return `note_${INoteType[noteType]}`;
  }, [noteType]);

  const titleField = useMemo(() => {
    if (noteType === INoteType.weather) {
      return (
        <BlockField label={t(`${fieldPrefix}_title`)} className="note-title-field">
          <AutocompleteInput defaultValue={canonicalNote?.title} name="title" onChange={onEdited} readOnly={!formEditable} suggestions={autocompleteWeatherNoteTitle} />
        </BlockField>
      );
    }
    return (
      <BlockField label={t(`${fieldPrefix}_title`)} className="note-title-field">
        <input defaultValue={canonicalNote?.title} name="title" onChange={onEdited} readOnly={!formEditable} />
      </BlockField>
    );
  }, [canonicalNote, formEditable, onEdited, fieldPrefix, noteType, autocompleteWeatherNoteTitle]);

  const noteCreator = useMemo(() => {
    return canonicalNote?.userId && shipUsers ? shipUsers.find((u) => u.id == canonicalNote.userId) : undefined;
  }, [canonicalNote, shipUsers]);

  return (
    <Block className={classNames(["NoteView", INoteType[noteType]])}>
      {!NoteOwnedByParent(parentType, canonicalNote) && (
        <BlockRow>
          <BlockField className="note-parent-info">
            {parentInfo}
          </BlockField>
        </BlockRow>
      )}
      <EditGroup buttonFirst={true} changed={edited} editable={formEditable} editableFields={editableFields} manualSave={!canonicalNote} onSave={saveEdits}>
        <input type="hidden" name="noteType" value={noteType} />
        <BlockRow>
          {contentFields.hasDate && (
            <BlockField label={t(`${fieldPrefix}_date`)} className="note-date-field">
              <input defaultValue={canonicalNote?.date} name="date" onChange={onEdited} readOnly={!formEditable} />
            </BlockField>
          )}
          <BlockField className="note-title-field">{titleField }</BlockField>

          {contentFields.noteIsInput && (
            <BlockField label={t(`${fieldPrefix}_note`)} className="note-note-field">
              <input defaultValue={canonicalNote?.note} name="note" onChange={onEdited} readOnly={!formEditable} />
            </BlockField>
          )}
        </BlockRow>
        {!contentFields.noteIsInput && (
          <BlockRow>
            <BlockField label={t(`${fieldPrefix}_note`)}>
              <div className="note-textarea-container">
                <textarea defaultValue={canonicalNote?.note} name="note" onChange={onEdited} readOnly={!formEditable} />
              </div>
            </BlockField>
          </BlockRow>
        )}

        {contentFields.hasNoteExtra && (
          <BlockRow className="noteextra-textarea-container">
            <BlockField label={t(`${fieldPrefix}_note_extra`)}>
              <div className="note-textarea-container">
                <textarea defaultValue={canonicalNote?.noteExtra} name="noteExtra" onChange={onEdited} readOnly={!formEditable} />
              </div>
            </BlockField>
          </BlockRow>
        )}
      </EditGroup>
      <BlockRow>
        {noteCreator && (
          <BlockField className="note-creator-info">{t("note_created_by", { name: noteCreator.name || noteCreator.shortname || "" })}</BlockField>
        )}
      </BlockRow>
      {formEditable && canonicalNote && (
        <EditGroup buttonFirst={true} changed={files.length > 0} className={"NoteView-attachment-form"} editable={true} editableFields={["attachment"]} onSave={saveAttachments}>
          <BlockRow>
            <div className="form-field file-field" onDrop={handleDrop} onDragOver={(event) => event.preventDefault()}>
              <label htmlFor="attachment" className="file-field-label" onClick={() => inputRef.current && inputRef.current.click()}><span>{t("file_field_drag_drop")}</span></label>
              <input ref={inputRef} type="file" hidden multiple name="attachment" onChange={handleSelect} />
            </div>
          </BlockRow>
          <BlockRow>
            {files.map((file, index) => <div key={index} className="attachment-item">{file.name}</div>)}
          </BlockRow>
        </EditGroup>
      )}
      {canonicalNote && (
        <NoteAttachments
          attachments={attachments}
          shipId={ship.id}
          noteId={canonicalNote.id}
          editable={formEditable}
          cloudSession={app.cloudSession()}
          removeAttachment={(attachmentId) => writers.deleteAttachment(canonicalNote.id, attachmentId)}
          removeConfirm={{
            title: t("attachment_remove_title"),
            message: t("attachment_remove_confirm"),
            confirmLabel: t("attachment_remove_accept"),
            cancelLabel: t("attachment_remove_cancel"),
            errorMessage: t("attachment_remove_error"),
          }}
        />
      )}
      <hr className="hr-gray"></hr>
    </Block>
  );
}
